import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Wallet, RefreshCw, Play, PlayCircle, X, RotateCcw,
  ShieldAlert, AlertTriangle, ChevronDown, ChevronRight,
  DollarSign, Lock, TrendingDown, CheckCircle2, Info,
  FileText, AlertCircle, PauseCircle, Download,
} from "lucide-react";
import { API } from "@/config";
import { Card, Btn } from "@/components/admin/ui";

/* -------------------------------------------------------------------------- */
/* Helpers                                                                      */
/* -------------------------------------------------------------------------- */
const money = (cents) => `$${((Number(cents) || 0) / 100).toFixed(2)}`;
const moneyD = (dollars) => `$${Number(dollars || 0).toFixed(2)}`;

const STATUS_TABS = [
  { key: "pending", label: "Pending" },
  { key: "processed", label: "Processed" },
  { key: "cancelled", label: "Cancelled" },
  { key: "failed", label: "Failed" },
];

const KIND_LABEL = {
  care: "Care fee",
  pom_fee: "PoM fee",
  pom_full: "PoM · full balance",
  standard_50_1: "Standard · 50% (day −15)",
  standard_50_2: "Standard · 50% (day −7)",
};

const KIND_BADGE = {
  care: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10",
  pom_fee: "text-[#FFD166] border-[#FFD166]/40 bg-[#FFD166]/10",
  pom_full: "text-[#FFD166] border-[#FFD166]/40 bg-[#FFD166]/10",
  standard_50_1: "text-[#B9C8D5] border-[#B9C8D5]/40 bg-black/25",
  standard_50_2: "text-[#B9C8D5] border-[#B9C8D5]/40 bg-black/25",
};

const STATUS_BADGE = {
  pending: "text-[#FFD166] border-[#FFD166]/40 bg-[#FFD166]/10",
  processed: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10",
  cancelled: "text-[#B9C8D5] border-[#B9C8D5]/30 bg-black/25",
  failed: "text-red-300 border-red-500/40 bg-red-500/10",
};

/* -------------------------------------------------------------------------- */
/* Sweep Paused Banner                                                          */
/* -------------------------------------------------------------------------- */
const SweepPausedBanner = ({ waterfall }) => {
  if (!waterfall) return null;
  const reasons = waterfall.sweep_blocked_reasons || [];
  if (reasons.length === 0 && waterfall.sweep_allowed) return null;

  const msgMap = {
    payout_sweep_disabled: "Live sweep is paused — enable it in Booking Settings → Finance once reconciliation is verified.",
    reconciliation_imbalance: `Stripe balance has $${((waterfall.reconciliation?.unattributed_cents || 0) / 100).toFixed(2)} that cannot be attributed to any payout schedule entry.`,
    needs_review_flags: `${waterfall.needs_review_count} unresolved Needs Review flag(s) must be resolved first.`,
  };

  return (
    <div className="rounded-xl border border-amber-500/40 bg-amber-500/8 p-4 space-y-1.5" data-testid="sweep-paused-banner">
      <div className="flex items-center gap-2">
        <PauseCircle className="w-4 h-4 text-amber-400 shrink-0" />
        <span className="text-sm font-medium text-amber-300">Live Sweep Paused — Fail-Closed</span>
      </div>
      <ul className="space-y-1">
        {reasons.map((r) => (
          <li key={r} className="text-xs text-amber-200/80 pl-6">· {msgMap[r] || r}</li>
        ))}
      </ul>
      <p className="text-[11px] text-amber-200/60 pl-6">Dry Run is always available. Enable live sweep only after reviewing the Reconciliation Report below.</p>
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/* Reconciliation Banner                                                        */
/* -------------------------------------------------------------------------- */
const ReconciliationBanner = ({ waterfall }) => {
  if (!waterfall?.reconciliation) return null;
  const { balanced, unattributed_cents } = waterfall.reconciliation;
  if (balanced) return null;

  return (
    <div className="rounded-xl border border-red-500/40 bg-red-500/8 p-4" data-testid="reconciliation-banner">
      <div className="flex items-start gap-2">
        <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
        <div>
          <p className="text-sm font-medium text-red-300">Reconciliation Imbalance — ${(unattributed_cents / 100).toFixed(2)} Unattributed</p>
          <p className="text-xs text-red-200/70 mt-1">
            The Stripe available balance exceeds the sum of all pending payout schedule entries by ${(unattributed_cents / 100).toFixed(2)}.
            This money cannot currently be traced to any scheduled payout. Live sweep is blocked until this is investigated and resolved.
          </p>
          <p className="text-xs text-red-200/60 mt-1">
            Common causes: payout schedule entries were cancelled without a corresponding bank transfer; payments received outside the normal booking flow; Stripe fees or adjustments. Review the Reconciliation Report tab for the per-reservation breakdown.
          </p>
        </div>
      </div>
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/* Waterfall Cards                                                              */
/* -------------------------------------------------------------------------- */
const WaterfallCards = ({ waterfall: wf, onNeedsReviewClick }) => {
  if (!wf) return null;

  const tax = wf.sales_tax || {};
  const outstandingLiabilityDollars = tax.outstanding_liability || 0;
  const protectedInStripeDollars = (tax.protected_in_stripe_cents || 0) / 100;
  const shortfallDollars = (tax.reserve_shortfall_cents || 0) / 100;

  const cards = [
    {
      label: "Total Stripe Balance",
      value: money(wf.stripe.total_cents),
      sub: `${money(wf.stripe.available_cents)} available`,
      color: "text-[#FFD166]",
      icon: Wallet,
      testid: "wf-stripe-total",
      tip: "Available + Pending Settlement in your connected Stripe account.",
    },
    {
      label: "Pending Settlement",
      value: money(wf.stripe.pending_settlement_cents),
      sub: "Processing by Stripe (T+2–5)",
      color: "text-[#B9C8D5]",
      icon: Lock,
      testid: "wf-pending-settlement",
      tip: "Funds captured but not yet released by Stripe's rolling settlement window.",
      deduction: true,
    },
    {
      label: "Protected by Policy",
      value: money(wf.payout_schedule.protected_cents),
      sub: "Inside cancellation window",
      color: "text-amber-300",
      icon: ShieldAlert,
      testid: "wf-protected",
      tip: "POM balances held until delivery day; standard 50% tranches not yet past day −15/−7.",
      deduction: true,
    },
    {
      label: "Eligible Under Policy",
      value: money(wf.payout_schedule.eligible_cents),
      sub: "Cancellation window cleared",
      color: "text-sky-300",
      icon: CheckCircle2,
      testid: "wf-eligible",
      tip: "Funds whose cancellation-protection window has expired and are ready for the deduction layer.",
    },
    {
      label: "FL Tax Liability",
      value: moneyD(outstandingLiabilityDollars),
      sub: shortfallDollars > 0
        ? `⚠ $${shortfallDollars.toFixed(2)} shortfall in Stripe`
        : `$${protectedInStripeDollars.toFixed(2)} covered in Stripe`,
      color: "text-orange-300",
      icon: TrendingDown,
      testid: "wf-sales-tax-liability",
      tip: `Outstanding FL DOR liability (tax collected net of refunds, minus remitted). Protected in Stripe: $${protectedInStripeDollars.toFixed(2)}. Shortfall: $${shortfallDollars.toFixed(2)}.`,
      deduction: true,
    },
    {
      label: "Federal Tax Reserve",
      value: money(wf.federal.reserve_cents),
      sub: `${Math.round(wf.federal.reserve_rate * 100)}% of net eligible`,
      color: "text-orange-300",
      icon: TrendingDown,
      testid: "wf-federal",
      tip: "Estimated SE + income tax reserve on eligible net earnings. Separate from customer tax. Rate configurable in Booking Settings.",
      deduction: true,
    },
    {
      label: "Needs Review",
      value: money(wf.payout_schedule.needs_review_cents),
      sub: `${wf.needs_review_res_count} reservation${wf.needs_review_res_count === 1 ? "" : "s"}`,
      color: wf.needs_review_count > 0 ? "text-red-300" : "text-[#B9C8D5]",
      icon: AlertTriangle,
      testid: "wf-needs-review",
      tip: "Eligible funds held because of unresolved system or manual flags. Click to see details.",
      deduction: true,
      clickable: wf.needs_review_count > 0,
      onClick: onNeedsReviewClick,
    },
    {
      label: "Available for Payout",
      value: money(wf.available_for_payout_cents),
      sub: wf.sweep_allowed ? "Ready to sweep to bank" : "Sweep paused",
      color: wf.sweep_allowed ? "text-emerald-300" : "text-[#B9C8D5]",
      icon: DollarSign,
      testid: "wf-available",
      tip: "Eligible − Sales Tax Reserve − Federal Reserve − Needs Review = this amount. Live sweep requires all gates to pass.",
      prominent: wf.sweep_allowed,
    },
  ];

  // Unattributed (error state) — shown only when non-zero
  const unattributed = wf.reconciliation?.unattributed_cents || 0;
  if (unattributed > 0) {
    cards.splice(3, 0, {
      label: "Unattributed / Needs Review",
      value: money(unattributed),
      sub: "Cannot trace to payout schedule",
      color: "text-red-300",
      icon: AlertCircle,
      testid: "wf-unattributed",
      tip: "Stripe available balance that cannot be matched to any pending payout schedule entry. This is an error/review state — not income. Live sweep is blocked until resolved.",
      error: true,
    });
  }

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      {cards.map((c) => (
        <Card
          key={c.label}
          className={`p-4 transition-colors ${c.clickable ? "cursor-pointer hover:border-red-400/40 hover:bg-red-500/5" : ""} ${c.prominent ? "ring-1 ring-emerald-500/30" : ""} ${c.error ? "ring-1 ring-red-500/40 bg-red-500/5" : ""}`}
          onClick={c.clickable ? c.onClick : undefined}
          data-testid={c.testid}
        >
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] uppercase tracking-wider text-[#B9C8D5] leading-tight">{c.label}</span>
            <c.icon className={`w-3.5 h-3.5 shrink-0 ${c.color}`} />
          </div>
          <div className={`font-serif text-xl mt-0.5 ${c.color}`}>{c.value}</div>
          <div className="text-[10px] text-[#B9C8D5] mt-1 leading-tight">{c.sub}</div>
        </Card>
      ))}
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/* Tax Detail Panel (collapsible)                                               */
/* -------------------------------------------------------------------------- */
const TaxDetailPanel = ({ tax }) => {
  const [open, setOpen] = useState(false);
  if (!tax) return null;
  return (
    <div className="rounded-xl border border-orange-500/20 bg-orange-500/5">
      <button
        className="w-full flex items-center justify-between p-3 text-left"
        onClick={() => setOpen((v) => !v)}
        data-testid="tax-detail-toggle"
      >
        <div className="flex items-center gap-2">
          <TrendingDown className="w-3.5 h-3.5 text-orange-300" />
          <span className="text-xs font-medium text-orange-300">FL Sales Tax Detail</span>
        </div>
        <ChevronDown className={`w-3.5 h-3.5 text-[#B9C8D5] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-1.5">
          <div className="grid sm:grid-cols-3 gap-2 pt-1">
            <div className="rounded-lg border border-orange-500/20 bg-black/20 p-2.5">
              <div className="text-[10px] uppercase text-[#B9C8D5]">Outstanding FL Tax Liability</div>
              <div className="font-serif text-lg text-orange-300">{moneyD(tax.outstanding_liability)}</div>
              <div className="text-[10px] text-[#B9C8D5] mt-0.5">Tax collected − refunded − remitted to FL DOR</div>
            </div>
            <div className="rounded-lg border border-sky-500/20 bg-black/20 p-2.5">
              <div className="text-[10px] uppercase text-[#B9C8D5]">Protected in Stripe</div>
              <div className="font-serif text-lg text-sky-300">{money(tax.protected_in_stripe_cents)}</div>
              <div className="text-[10px] text-[#B9C8D5] mt-0.5">Portion of Stripe available covering tax</div>
            </div>
            <div className={`rounded-lg border p-2.5 ${(tax.reserve_shortfall_cents || 0) > 0 ? "border-red-500/30 bg-red-500/5" : "border-emerald-500/20 bg-black/20"}`}>
              <div className="text-[10px] uppercase text-[#B9C8D5]">Reserve Shortfall</div>
              <div className={`font-serif text-lg ${(tax.reserve_shortfall_cents || 0) > 0 ? "text-red-300" : "text-emerald-300"}`}>{money(tax.reserve_shortfall_cents)}</div>
              <div className="text-[10px] text-[#B9C8D5] mt-0.5">Liability not yet covered in Stripe</div>
            </div>
          </div>
          <div className="text-[10px] text-[#B9C8D5] pt-1 space-y-0.5">
            <div>Gross collected: <span className="text-[#F6F1E4]">{moneyD(tax.collected_gross)}</span></div>
            <div>Refunded to customers: <span className="text-[#F6F1E4]">−{moneyD(tax.refunded_tax)}</span></div>
            <div>Net collected: <span className="text-[#F6F1E4]">{moneyD(tax.collected_net)}</span></div>
            <div>Remitted to FL DOR: <span className="text-emerald-300">−{moneyD(tax.remitted_total)}</span></div>
            <div className="border-t border-[#FFD166]/10 pt-0.5 font-medium">Outstanding: <span className="text-orange-300">{moneyD(tax.outstanding_liability)}</span></div>
          </div>
        </div>
      )}
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/* Reconciliation Report Panel                                                  */
/* -------------------------------------------------------------------------- */
const ReconciliationReportPanel = ({ onClose }) => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/admin/payouts/reconciliation-report`)
      .then((r) => setReport(r.data))
      .catch(() => toast.error("Couldn't load reconciliation report"))
      .finally(() => setLoading(false));
  }, []);

  const downloadCSV = () => {
    if (!report?.rows?.length) return;
    const headers = ["Reservation", "Customer", "Delivery Date", "Status", "Paid", "Tax", "Refund", "Net Retained", "Protected", "Eligible", "Tax Reserve", "Federal Reserve", "Payout Eligible"];
    const rows = report.rows.map((row) => [
      row.short_code || (row.reservation_id || "").slice(0, 8).toUpperCase(),
      row.customer_name || "",
      row.delivery_date || "",
      row.status || "",
      Number(row.customer_paid || 0).toFixed(2),
      Number(row.tax_collected || 0).toFixed(2),
      Number(row.admin_refund_total || 0).toFixed(2),
      Number(row.net_retained || 0).toFixed(2),
      Number(row.cancellation_protected || 0).toFixed(2),
      Number(row.eligible_gross || 0).toFixed(2),
      Number(row.tax_reserve_applied || 0).toFixed(2),
      Number(row.federal_reserve || 0).toFixed(2),
      Number(row.currently_eligible_for_payout || 0).toFixed(2),
    ]);
    const csv = [headers, ...rows].map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `reconciliation-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading) return (
    <div className="rounded-xl border border-[#FFD166]/20 bg-black/30 p-6 text-center text-[#B9C8D5] text-sm" data-testid="recon-report-loading">
      Building reconciliation report…
    </div>
  );
  if (!report) return null;

  const t = report.totals || {};
  const sr = report.stripe_reconciliation || {};
  const tax = report.tax_liability_summary || {};

  return (
    <div className="rounded-xl border border-[#FFD166]/20 bg-black/30 p-5 space-y-5" data-testid="recon-report-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-[#FFD166]" />
          <span className="font-serif text-lg text-[#FFD166]">Reconciliation Report</span>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-[10px] px-2 py-1 rounded-full border ${sr.balanced ? "text-emerald-300 border-emerald-500/40 bg-emerald-500/10" : "text-red-300 border-red-500/40 bg-red-500/10"}`}>
            {sr.balanced ? "Balanced" : "IMBALANCE"}
          </span>
          <button onClick={downloadCSV} title="Export CSV" className="text-[#B9C8D5] hover:text-[#FFD166] transition-colors" data-testid="recon-csv-btn">
            <Download className="w-4 h-4" />
          </button>
          <button onClick={onClose} className="text-[#B9C8D5] hover:text-[#F6F1E4]"><X className="w-4 h-4" /></button>
        </div>
      </div>

      {/* Stripe proof */}
      <div className="rounded-lg border border-[#FFD166]/15 bg-black/20 p-3 text-xs space-y-1">
        <p className="text-[#B9C8D5] font-medium uppercase tracking-wider text-[10px] mb-2">Stripe Reconciliation</p>
        <div className="flex justify-between"><span className="text-[#B9C8D5]">Stripe available balance</span><span className="text-[#F6F1E4] font-mono">{moneyD(sr.stripe_available)}</span></div>
        <div className="flex justify-between"><span className="text-[#B9C8D5]">Attributed (pending schedule)</span><span className="text-[#F6F1E4] font-mono">{money(sr.attributed_cents)}</span></div>
        <div className={`flex justify-between border-t border-[#FFD166]/10 pt-1 ${sr.balanced ? "text-emerald-300" : "text-red-300"}`}>
          <span>Unattributed</span><span className="font-mono">{money(sr.unattributed_cents)}</span>
        </div>
      </div>

      {/* Tax summary */}
      <div className="rounded-lg border border-orange-500/20 bg-black/20 p-3 text-xs space-y-1">
        <p className="text-[#B9C8D5] font-medium uppercase tracking-wider text-[10px] mb-2">Tax Liability Summary</p>
        <div className="flex justify-between"><span className="text-[#B9C8D5]">Gross collected</span><span className="text-[#F6F1E4] font-mono">{moneyD(tax.total_collected_gross)}</span></div>
        <div className="flex justify-between"><span className="text-[#B9C8D5]">Refunded to customers</span><span className="text-red-300 font-mono">−{moneyD(tax.total_refunded_tax)}</span></div>
        <div className="flex justify-between"><span className="text-[#B9C8D5]">Net retained</span><span className="text-[#F6F1E4] font-mono">{moneyD(tax.total_collected_net)}</span></div>
        <div className="flex justify-between"><span className="text-[#B9C8D5]">Remitted to FL DOR</span><span className="text-emerald-300 font-mono">−{moneyD(tax.total_remitted)}</span></div>
        <div className="flex justify-between border-t border-orange-500/20 pt-1 text-orange-300 font-medium"><span>Outstanding liability</span><span className="font-mono">{moneyD(tax.outstanding_liability)}</span></div>
      </div>

      {/* Aggregate totals */}
      <div className="rounded-lg border border-[#B9C8D5]/15 bg-black/20 p-3 text-xs space-y-1">
        <p className="text-[#B9C8D5] font-medium uppercase tracking-wider text-[10px] mb-2">Aggregate Totals ({report.rows?.length || 0} reservations)</p>
        <div className="flex justify-between"><span className="text-[#B9C8D5]">Customer paid</span><span className="text-[#FFD166] font-mono">{moneyD(t.customer_paid)}</span></div>
        <div className="flex justify-between"><span className="text-[#B9C8D5]">Tax collected</span><span className="text-orange-300 font-mono">{moneyD(t.tax_collected)}</span></div>
        <div className="flex justify-between"><span className="text-[#B9C8D5]">Refunds issued</span><span className="text-red-300 font-mono">−{moneyD(t.admin_refund_total)}</span></div>
        <div className="flex justify-between"><span className="text-[#B9C8D5]">Cancellation protected</span><span className="text-amber-300 font-mono">{moneyD(t.cancellation_protected)}</span></div>
        <div className="flex justify-between"><span className="text-[#B9C8D5]">Eligible gross</span><span className="text-sky-300 font-mono">{moneyD(t.eligible_gross)}</span></div>
        <div className="flex justify-between"><span className="text-[#B9C8D5]">Previously paid to bank</span><span className="text-emerald-300 font-mono">{moneyD(t.previously_paid_to_bank)}</span></div>
        <div className="flex justify-between border-t border-[#B9C8D5]/15 pt-1"><span className="text-[#B9C8D5]">Currently eligible for payout</span><span className="text-emerald-300 font-mono">{moneyD(t.currently_eligible_for_payout)}</span></div>
      </div>

      {/* Per-reservation table */}
      <div>
        <p className="text-[10px] uppercase tracking-wider text-[#B9C8D5] mb-2">Per-Reservation Detail</p>
        <div className="overflow-x-auto">
          <table className="w-full text-[11px]" data-testid="recon-report-table">
            <thead>
              <tr className="text-[9px] uppercase tracking-wider text-[#B9C8D5] border-b border-[#FFD166]/10">
                <th className="text-left pb-2 pr-3">Reservation</th>
                <th className="text-left pb-2 pr-3">Status</th>
                <th className="text-right pb-2 pr-3">Paid</th>
                <th className="text-right pb-2 pr-3">Tax</th>
                <th className="text-right pb-2 pr-3">Refund</th>
                <th className="text-right pb-2 pr-3">Net Retained</th>
                <th className="text-right pb-2 pr-3">Protected</th>
                <th className="text-right pb-2 pr-3">Eligible</th>
                <th className="text-right pb-2 pr-3">Tax Reserve</th>
                <th className="text-right pb-2 pr-3">Federal Reserve</th>
                <th className="text-right pb-2">Payout Eligible</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#FFD166]/5">
              {(report.rows || []).map((row) => (
                <tr key={row.reservation_id} data-testid={`recon-row-${row.reservation_id}`} className="hover:bg-white/2">
                  <td className="py-1.5 pr-3">
                    <div className="text-[#FFD166] font-mono">{row.short_code || row.reservation_id?.slice(0,8).toUpperCase()}</div>
                    <div className="text-[#B9C8D5]/70">{row.customer_name}</div>
                    <div className="text-[#B9C8D5]/50">{row.delivery_date}</div>
                  </td>
                  <td className="py-1.5 pr-3">
                    <span className={`text-[9px] px-1.5 py-0.5 rounded border ${row.status === "cancelled" ? "text-red-300 border-red-500/30" : "text-emerald-300 border-emerald-500/30"}`}>
                      {row.status}
                    </span>
                  </td>
                  <td className="py-1.5 pr-3 text-right text-[#F6F1E4] tabular-nums">{moneyD(row.customer_paid)}</td>
                  <td className="py-1.5 pr-3 text-right text-orange-300 tabular-nums">{moneyD(row.tax_collected)}</td>
                  <td className="py-1.5 pr-3 text-right text-red-300 tabular-nums">{row.admin_refund_total > 0 ? `−${moneyD(row.admin_refund_total)}` : "—"}</td>
                  <td className="py-1.5 pr-3 text-right text-[#F6F1E4] tabular-nums">{moneyD(row.net_retained)}</td>
                  <td className="py-1.5 pr-3 text-right text-amber-300 tabular-nums">{moneyD(row.cancellation_protected)}</td>
                  <td className="py-1.5 pr-3 text-right text-sky-300 tabular-nums">{moneyD(row.eligible_gross)}</td>
                  <td className="py-1.5 pr-3 text-right text-orange-300 tabular-nums">{moneyD(row.tax_reserve_applied)}</td>
                  <td className="py-1.5 pr-3 text-right text-orange-300 tabular-nums">{moneyD(row.federal_reserve)}</td>
                  <td className="py-1.5 text-right text-emerald-300 tabular-nums font-medium">{moneyD(row.currently_eligible_for_payout)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-[10px] text-[#B9C8D5] italic">
        Generated {new Date(report.generated_at).toLocaleString()}. This report is read-only — no data has been modified.
      </p>
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/* Needs Review Panel                                                           */
/* -------------------------------------------------------------------------- */
const NeedsReviewPanel = ({ items, onClose, onReload }) => {
  const [resolving, setResolving] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const resolve = async (flagId, resId) => {
    if (!note.trim()) { toast.error("A resolution note is required."); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/admin/reservations/${resId}/needs-review/${flagId}/resolve`, { resolution_note: note });
      toast.success("Flag resolved — funds will be included in the next sweep.");
      setResolving(null); setNote("");
      onReload?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Resolve failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="border border-red-500/30 rounded-xl bg-red-500/5 p-5 space-y-3" data-testid="needs-review-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-red-300">
          <AlertTriangle className="w-4 h-4" />
          <span className="font-medium text-sm">Needs Review — funds held until resolved</span>
        </div>
        <button onClick={onClose} className="text-[#B9C8D5] hover:text-[#F6F1E4]"><X className="w-4 h-4" /></button>
      </div>
      <p className="text-[11px] text-[#B9C8D5]">
        The amounts below are excluded from the payout sweep (fail-closed). Each flag must be manually resolved with a note before those funds become Available for Payout.
      </p>
      <div className="space-y-2">
        {items.map((item) => (
          <div key={item.flag_id} className="rounded-lg border border-red-500/20 bg-black/20 p-3" data-testid={`nr-item-${item.flag_id}`}>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <span className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded border mr-2 ${item.flag_type === "manual" ? "text-amber-300 border-amber-500/40 bg-amber-500/10" : "text-red-300 border-red-500/40 bg-red-500/10"}`}>
                  {item.flag_type === "manual" ? "Manual" : "System"}
                </span>
                <span className="text-[11px] text-[#B9C8D5] font-mono">{item.condition_key}</span>
              </div>
              <span className="font-serif text-base text-red-300">${Number(item.amount_held).toFixed(2)}</span>
            </div>
            <p className="text-sm text-[#F6F1E4] mt-1">{item.reason}</p>
            <p className="text-[10px] text-[#B9C8D5] mt-1">
              Flagged {new Date(item.flagged_at).toLocaleString()} · by {item.flagged_by}
            </p>
            <p className="text-[10px] text-[#B9C8D5]">Reservation: <span className="text-[#FFD166] font-mono">{item.reservation_id.slice(0,8).toUpperCase()}</span></p>
            {resolving === item.flag_id ? (
              <div className="mt-2 space-y-2">
                <textarea rows={2} className="w-full rounded-lg bg-black/40 border border-[#FFD166]/20 text-sm text-[#F6F1E4] p-2 placeholder-[#B9C8D5]/50 resize-none" placeholder="Describe what was verified and why it is safe to release these funds…" value={note} onChange={(e) => setNote(e.target.value)} data-testid={`nr-resolve-note-${item.flag_id}`} />
                <div className="flex gap-2">
                  <button onClick={() => { setResolving(null); setNote(""); }} className="text-xs px-3 py-1.5 rounded-lg border border-[#B9C8D5]/25 text-[#B9C8D5]">Cancel</button>
                  <button onClick={() => resolve(item.flag_id, item.reservation_id)} disabled={busy} className="text-xs px-3 py-1.5 rounded-lg bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 disabled:opacity-50" data-testid={`nr-confirm-resolve-${item.flag_id}`}>
                    {busy ? "Resolving…" : "Confirm resolution"}
                  </button>
                </div>
              </div>
            ) : (
              <button onClick={() => { setResolving(item.flag_id); setNote(""); }} className="mt-2 text-xs px-3 py-1.5 rounded-lg border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10" data-testid={`nr-resolve-btn-${item.flag_id}`}>
                Resolve with note
              </button>
            )}
          </div>
        ))}
        {items.length === 0 && <p className="text-[#B9C8D5] text-sm">No active flags.</p>}
      </div>
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/* Per-Reservation Breakdown Panel (shown inline in payout row)                */
/* -------------------------------------------------------------------------- */
const ReservationBreakdownPanel = ({ reservationId, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/admin/reservations/${reservationId}/financial-breakdown`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Couldn't load breakdown"))
      .finally(() => setLoading(false));
  }, [reservationId]);

  if (loading) return <div className="py-4 text-[#B9C8D5] text-sm text-center">Loading…</div>;
  if (!data) return null;

  const Row = ({ label, value, color, bold, indent, testid }) => (
    <div className={`flex justify-between py-1 ${indent ? "pl-4" : ""} ${bold ? "border-t border-[#FFD166]/10 mt-1 pt-2" : ""}`} data-testid={testid}>
      <span className={`text-xs ${bold ? "text-[#F6F1E4] font-medium" : "text-[#B9C8D5]"}`}>{label}</span>
      <span className={`text-xs tabular-nums font-mono ${color || (bold ? "text-[#F6F1E4]" : "text-[#B9C8D5]")}`}>{value}</span>
    </div>
  );

  const pct = (r) => `${Math.round((r || 0) * 100)}%`;

  return (
    <div className="mt-3 rounded-xl border border-[#FFD166]/15 bg-black/30 p-4" data-testid={`breakdown-panel-${reservationId}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs uppercase tracking-wider text-[#FFD166]">Financial Breakdown · {data.short_code || data.reservation_id?.slice(0,8).toUpperCase()}</span>
        <button onClick={onClose} className="text-[#B9C8D5] hover:text-[#F6F1E4]"><X className="w-3.5 h-3.5" /></button>
      </div>

      <div className="space-y-0.5">
        <Row label="Customer total charged" value={moneyD(data.customer_total_charged)} bold color="text-[#FFD166]" testid="bd-total" />
        <Row label="Rental subtotal" value={moneyD(data.rental_subtotal)} indent />
        <Row label="Delivery fee" value={moneyD(data.delivery_fee)} indent />
        <Row label="Sales tax collected" value={moneyD(data.sales_tax_collected)} indent color="text-orange-300" />
        {data.driver_tip > 0 && <Row label="Driver tip" value={moneyD(data.driver_tip)} indent />}
        {data.discounts > 0 && <Row label="Discounts" value={`-${moneyD(data.discounts)}`} indent color="text-emerald-300" />}
        {data.refund_amount > 0 && <Row label="Refunds issued" value={`-${moneyD(data.refund_amount)}`} indent color="text-red-300" />}
        {data.affiliate_commission > 0 && <Row label="Affiliate commission" value={moneyD(data.affiliate_commission)} indent color="text-purple-300" />}
      </div>

      {data.has_pom && (
        <div className="mt-3 pt-2 border-t border-[#FFD166]/10 space-y-0.5">
          <p className="text-[10px] uppercase tracking-wider text-[#B9C8D5] mb-1">Cancellation Protection Split</p>
          <Row label="PoM fee (non-refundable)" value={moneyD(data.pom_fee)} color="text-[#FFD166]" testid="bd-pom-fee" />
          {data.pom_fee_payout && <Row label={`  → ${data.pom_fee_payout.status}`} value={data.pom_fee_payout.scheduled_for || "—"} indent />}
          <Row label="PoM balance (held until delivery)" value={moneyD(data.pom_full_payout?.amount || 0)} />
          {data.pom_full_payout && <Row label={`  → ${data.pom_full_payout.status} (${data.pom_full_payout.scheduled_for || "?"})`} value="" indent />}
        </div>
      )}

      <div className="mt-3 pt-2 border-t border-[#FFD166]/10 space-y-0.5">
        <p className="text-[10px] uppercase tracking-wider text-[#B9C8D5] mb-1">Payout Schedule</p>
        <Row label="Already paid to bank" value={moneyD(data.already_paid_out)} color="text-emerald-300" testid="bd-paid-out" />
        <Row label="Protected (inside cancel window)" value={moneyD(data.cancellation_protected)} color="text-amber-300" />
        <Row label="Eligible (window cleared)" value={moneyD(data.eligible_payout_gross)} color="text-sky-300" testid="bd-eligible" />
        {data.next_payout_date && (
          <Row label={`Next release`} value={`${moneyD(data.next_payout_amount)} on ${data.next_payout_date}`} indent />
        )}
        {data.payout_entries.map((e) => (
          <Row key={e.id} label={`  ${KIND_LABEL[e.kind] || e.kind}`}
               value={`${moneyD(e.amount)} · ${e.status}`}
               indent color={e.status === "processed" ? "text-emerald-300" : e.status === "failed" ? "text-red-300" : "text-[#B9C8D5]"} />
        ))}
      </div>

      <div className="mt-3 pt-2 border-t border-[#FFD166]/10 space-y-0.5">
        <p className="text-[10px] uppercase tracking-wider text-[#B9C8D5] mb-1">Reserves</p>
        <Row label="Sales tax reserve" value={moneyD(data.sales_tax_reserve)} color="text-orange-300" testid="bd-tax-reserve" />
        <Row label={`Federal reserve (${pct(data.federal_reserve_rate)})`} value={moneyD(data.federal_reserve)} color="text-orange-300" testid="bd-fed-reserve" />
        {data.needs_review_amount > 0 && (
          <Row label="Needs Review hold" value={moneyD(data.needs_review_amount)} color="text-red-300" />
        )}
        <Row label="Available for payout" value={moneyD(data.available_for_payout)} bold color="text-emerald-300" testid="bd-available" />
      </div>

      <div className="mt-3 pt-2 border-t border-[#FFD166]/10">
        <div className="flex items-center gap-1.5 text-[10px] text-[#B9C8D5]">
          {data.reconciliation.balanced
            ? <><CheckCircle2 className="w-3 h-3 text-emerald-400" /> Reconciliation balanced</>
            : <><AlertTriangle className="w-3 h-3 text-amber-400" /> Reconciliation gap: {moneyD(Math.abs(data.reconciliation.customer_paid - data.reconciliation.check_sum))}</>
          }
        </div>
      </div>
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/* Main Payouts page                                                            */
/* -------------------------------------------------------------------------- */
export const Payouts = () => {
  const [tab, setTab] = useState("pending");
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [waterfall, setWaterfall] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [nrOpen, setNrOpen] = useState(false);
  const [reconOpen, setReconOpen] = useState(true);
  const [expandedRow, setExpandedRow] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [entriesRes, wfRes] = await Promise.all([
        axios.get(`${API}/admin/payouts`, { params: { status: tab } }),
        axios.get(`${API}/admin/payouts/waterfall`),
      ]);
      setRows(entriesRes.data.entries || []);
      setSummary(entriesRes.data.summary || {});
      setWaterfall(wfRes.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load payouts");
    } finally { setLoading(false); }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const runSweep = async ({ dryRun = false } = {}) => {
    if (!dryRun && !window.confirm("Fire the nightly Stripe payout sweep now? This will move money out of your Stripe balance to your bank.")) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/payouts/run`, null, { params: { dry_run: dryRun } });
      // Handle blocked response
      if (r.data.blocked) {
        toast.error(r.data.message || `Sweep blocked: ${r.data.reason}`);
        return;
      }
      const skipped = r.data.skipped_needs_review_count || 0;
      const skippedAmt = r.data.skipped_needs_review_cents || 0;
      if (dryRun) {
        toast.success(
          `Dry-run — would settle ${r.data.would_process_count || 0} entries · ${money(r.data.would_process_total_cents || 0)}.` +
          (skipped > 0 ? ` (${skipped} Needs Review reservations skipped · ${money(skippedAmt)})` : "")
        );
      } else if (r.data.payout_id) {
        toast.success(`Payout ${r.data.payout_id} · ${money(r.data.total_cents)} · ${r.data.processed_count} entries` +
          (skipped > 0 ? ` · ${skipped} skipped (Needs Review)` : ""));
      } else {
        toast.info(r.data.reason || "Nothing due");
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sweep failed");
    } finally { setBusy(false); }
  };

  const scanForIssues = async () => {
    setScanning(true);
    try {
      const r = await axios.post(`${API}/admin/payouts/needs-review/scan`);
      if (r.data.new_flags_created > 0) {
        toast.warning(`Scan complete — ${r.data.new_flags_created} new issue${r.data.new_flags_created !== 1 ? "s" : ""} flagged for review.`);
      } else {
        toast.success(`Scan complete — no new issues found (${r.data.conditions_checked} checked).`);
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Scan failed");
    } finally { setScanning(false); }
  };

  const cancelEntry = async (id) => {
    if (!window.confirm("Cancel this pending payout entry? The money stays in Stripe.")) return;
    try {
      await axios.post(`${API}/admin/payouts/${id}/cancel`);
      toast.success("Cancelled");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Cancel failed"); }
  };

  const retryEntry = async (id) => {
    try {
      await axios.post(`${API}/admin/payouts/${id}/retry`);
      toast.success("Retrying on next sweep");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Retry failed"); }
  };

  const dueToday = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return rows.filter((r) => r.status === "pending" && r.scheduled_for <= today)
               .reduce((a, r) => a + (r.amount_cents || 0), 0);
  }, [rows]);

  const hasNeedsReview = waterfall?.needs_review_count > 0;

  return (
    <div className="space-y-6" data-testid="admin-payouts-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-serif text-4xl">Payouts</h1>
          <p className="text-sm text-[#B9C8D5] mt-1">Stripe manual-payout schedule · nightly sweep at 1:00 AM ET</p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <Btn variant="ghost" onClick={load} disabled={loading} data-testid="payouts-refresh">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Btn>
          <Btn variant="ghost" onClick={scanForIssues} disabled={scanning} data-testid="payouts-scan">
            <AlertTriangle className="w-3.5 h-3.5" /> {scanning ? "Scanning…" : "Scan for issues"}
          </Btn>
          <Btn
            variant="ghost"
            onClick={() => setReconOpen((v) => !v)}
            data-testid="payouts-recon-btn"
          >
            <FileText className="w-3.5 h-3.5" /> Reconciliation Report
          </Btn>
          <Btn variant="ghost" onClick={() => runSweep({ dryRun: true })} disabled={busy} data-testid="payouts-dry-run">
            <PlayCircle className="w-3.5 h-3.5" /> Dry-run
          </Btn>
          <Btn
            onClick={() => runSweep()}
            disabled={busy || !waterfall?.sweep_allowed}
            className={!waterfall?.sweep_allowed ? "opacity-50 cursor-not-allowed" : ""}
            data-testid="payouts-run-sweep"
          >
            <Play className="w-3.5 h-3.5" /> {busy ? "Sweeping…" : "Run sweep now"}
          </Btn>
        </div>
      </div>

      {/* Sweep Paused Banner */}
      {waterfall && !waterfall.sweep_allowed && (
        <SweepPausedBanner waterfall={waterfall} />
      )}

      {/* Reconciliation Imbalance Banner */}
      {waterfall && !waterfall.reconciliation?.balanced && (
        <ReconciliationBanner waterfall={waterfall} />
      )}

      {/* Waterfall Cards */}
      <WaterfallCards
        waterfall={waterfall}
        onNeedsReviewClick={() => setNrOpen((v) => !v)}
      />

      {/* FL Tax Detail Panel */}
      {waterfall && <TaxDetailPanel tax={waterfall.sales_tax} />}

      {/* Needs Review Alert Banner */}
      {hasNeedsReview && !nrOpen && (
        <button
          onClick={() => setNrOpen(true)}
          className="w-full flex items-center gap-3 p-3 rounded-xl border border-red-500/40 bg-red-500/8 text-left hover:bg-red-500/12 transition-colors"
          data-testid="nr-banner"
        >
          <AlertTriangle className="w-4 h-4 text-red-400 shrink-0" />
          <span className="text-sm text-red-300">
            <strong>{waterfall.needs_review_count} Needs Review flag{waterfall.needs_review_count !== 1 ? "s" : ""}</strong> — {money(waterfall.payout_schedule.needs_review_cents)} is being held back from the next sweep. Click to review and resolve.
          </span>
          <ChevronRight className="w-4 h-4 text-red-400 ml-auto shrink-0" />
        </button>
      )}

      {/* Needs Review Expanded Panel */}
      {nrOpen && waterfall && (
        <NeedsReviewPanel
          items={waterfall.needs_review_items || []}
          onClose={() => setNrOpen(false)}
          onReload={load}
        />
      )}

      {/* Reconciliation Report Panel */}
      {reconOpen && (
        <ReconciliationReportPanel onClose={() => setReconOpen(false)} />
      )}

      {/* Summary tiles */}
      <div className="grid sm:grid-cols-3 gap-3">
        <Card>
          <div className="p-4">
            <div className="text-xs uppercase tracking-wide text-[#B9C8D5]">Due today or earlier</div>
            <div className="mt-1 font-serif text-2xl text-[#FFD166]" data-testid="payouts-due-today">{money(dueToday)}</div>
            <div className="text-[11px] text-[#B9C8D5] mt-1">Ready for next 1 AM run</div>
          </div>
        </Card>
        <Card>
          <div className="p-4">
            <div className="text-xs uppercase tracking-wide text-[#B9C8D5]">All pending</div>
            <div className="mt-1 font-serif text-2xl text-[#F6F1E4]">{money(summary.pending?.total_cents || 0)}</div>
            <div className="text-[11px] text-[#B9C8D5] mt-1">{summary.pending?.count || 0} entries scheduled</div>
          </div>
        </Card>
        <Card>
          <div className="p-4">
            <div className="text-xs uppercase tracking-wide text-[#B9C8D5]">Lifetime settled</div>
            <div className="mt-1 font-serif text-2xl text-emerald-300">{money(summary.processed?.total_cents || 0)}</div>
            <div className="text-[11px] text-[#B9C8D5] mt-1">
              {summary.failed?.count ? `${summary.failed.count} failed · ${money(summary.failed.total_cents)}` : "No failures on record"}
            </div>
          </div>
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 flex-wrap" data-testid="payouts-tabs">
        {STATUS_TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 rounded-full text-xs uppercase tracking-wide border ${
              tab === t.key
                ? "bg-[#FFD166]/15 border-[#FFD166]/60 text-[#FFD166]"
                : "border-[#B9C8D5]/25 text-[#B9C8D5] hover:text-[#FFD166] hover:border-[#FFD166]/40"
            }`}
            data-testid={`payouts-tab-${t.key}`}
          >
            {t.label}
            <span className="ml-1.5 opacity-70">({summary[t.key]?.count || 0})</span>
          </button>
        ))}
      </div>

      {/* Entries */}
      {rows.length === 0 ? (
        <div className="text-center py-16 text-[#B9C8D5]" data-testid="payouts-empty">
          {loading ? "Loading…" : `No ${tab} payouts.`}
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => (
            <div key={r.id} data-testid={`payout-row-${r.id}`}>
              <Card className="p-4">
                <div className="grid md:grid-cols-[1fr_auto] gap-3 items-center">
                  <button
                    className="text-left"
                    onClick={() => setExpandedRow(expandedRow === r.id ? null : r.id)}
                    data-testid={`payout-expand-${r.id}`}
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`text-[10px] uppercase tracking-wide px-2 py-1 rounded-full border ${KIND_BADGE[r.kind] || KIND_BADGE.standard_50_1}`}>
                        {KIND_LABEL[r.kind] || r.kind}
                      </span>
                      <span className={`text-[10px] uppercase tracking-wide px-2 py-1 rounded-full border ${STATUS_BADGE[r.status]}`}>
                        {r.status}
                      </span>
                      <span className="font-serif text-xl text-[#F6F1E4]">${Number(r.amount || 0).toFixed(2)}</span>
                      <ChevronDown className={`w-3.5 h-3.5 text-[#B9C8D5] ml-auto transition-transform ${expandedRow === r.id ? "rotate-180" : ""}`} />
                    </div>
                    <div className="text-sm text-[#B9C8D5] mt-1">
                      {r.customer_name || "—"} · {r.customer_email || "no email"} · delivery {r.delivery_date || "?"}
                    </div>
                    <div className="text-[11px] text-[#B9C8D5]/80 mt-0.5">
                      scheduled for <span className="text-[#F6F1E4]">{r.scheduled_for}</span>
                      {r.processed_at && <> · processed {new Date(r.processed_at).toLocaleString()}</>}
                      {r.stripe_payout_id && <> · {r.stripe_payout_id}</>}
                    </div>
                    {r.stripe_error && <div className="text-[11px] text-red-300 mt-1">Stripe: {r.stripe_error}</div>}
                    {r.note && r.status !== "pending" && <div className="text-[11px] text-[#B9C8D5]/70 mt-1 italic">{r.note}</div>}
                  </button>
                  <div className="flex gap-2 justify-end">
                    {r.status === "pending" && (
                      <button onClick={() => cancelEntry(r.id)} className="text-xs px-3 py-1.5 rounded-lg border border-red-500/40 text-red-300 hover:bg-red-500/10 inline-flex items-center gap-1.5" data-testid={`payout-cancel-${r.id}`}>
                        <X className="w-3 h-3" /> Cancel
                      </button>
                    )}
                    {r.status === "failed" && (
                      <button onClick={() => retryEntry(r.id)} className="text-xs px-3 py-1.5 rounded-lg border border-[#FFD166]/40 text-[#FFD166] hover:bg-[#FFD166]/10 inline-flex items-center gap-1.5" data-testid={`payout-retry-${r.id}`}>
                        <RotateCcw className="w-3 h-3" /> Retry
                      </button>
                    )}
                  </div>
                </div>
              </Card>
              {expandedRow === r.id && (
                <ReservationBreakdownPanel
                  reservationId={r.reservation_id}
                  onClose={() => setExpandedRow(null)}
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Payouts;
